import { Button, Field, FieldError, FieldLabel, Input } from "@/components/ui";

import { formatDate } from "@/lib/time";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";

import { useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { patientSearchFormSchema } from "./schemas";

interface PatientSearchFormProps {
  onSubmit(v: z.infer<typeof patientSearchFormSchema>): void;
  isLoading: boolean;
}

export const PatientSearchForm = ({
  onSubmit,
  isLoading,
}: PatientSearchFormProps) => {
  const { patientId, patientFirstName, patientLastName, dob } = useSearch({
    from: "/search",
  });

  const form = useForm<z.infer<typeof patientSearchFormSchema>>({
    resolver: zodResolver(patientSearchFormSchema),
    mode: "onSubmit",
    defaultValues: {
      id: patientId || "",
      firstName: patientFirstName || "",
      lastName: patientLastName || "",
      dob: dob ? format(new Date(dob), "MM/dd/yyyy") : "",
    },
  });

  // apply global error message to the form if no fields are inputed
  const { dob: _dobError, ...restErrors } = form.formState.errors;
  const globalError = !!Object.keys(restErrors).length;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-1"
      data-testid="patient-search-form"
    >
      <div className="flex items-end gap-3 w-fit">
        <Controller
          control={form.control}
          name="id"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="patientId">Patient ID</FieldLabel>
              <Input
                {...field}
                id="patientId"
                className="w-full"
                aria-invalid={fieldState.invalid}
              />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="firstName"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="patientFirstName">First Name</FieldLabel>
              <Input
                {...field}
                id="patientFirstName"
                className="w-full"
                aria-invalid={fieldState.invalid}
              />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="lastName"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="patientLastName">Last Name</FieldLabel>
              <Input
                {...field}
                id="patientLastName"
                className="w-full"
                aria-invalid={fieldState.invalid}
              />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="dob"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="patientDob">Date of Birth</FieldLabel>
              <Input
                {...field}
                id="patientDob"
                className="w-full"
                placeholder="mm/dd/yyyy"
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                onBlur={(e) => {
                  if (e.target.value.length === 0) {
                    field.onChange("");
                    return;
                  }
                  try {
                    const formattedValue = formatDate(e.target.value);
                    field.onChange(formattedValue);
                  } catch {
                    // Keep the invalid value so validation can catch it
                    field.onChange(e.target.value);
                  }
                }}
                type="text"
                aria-invalid={fieldState.invalid}
              />
              {!globalError && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Button
          type="submit"
          className="w-fit h-fit leading-tight"
          isLoading={isLoading}
        >
          Search
        </Button>
      </div>
    </form>
  );
};
